import { VStack } from "apps/commons/components/Stack";
import type { PayoutItem } from "libs/codegen/property-onboarding";
import { Fragment, type FunctionComponent } from "react";
import { AccommodationCategoryEnum } from "../../constants/accommodationCategoryEnum.ts";
import { useMetadataAtomValue } from "../../metadataAtom.ts";
import { useStaticDataAtomValue } from "../../staticDataAtom";
import { useAccommodationCategory } from "../../useAccommodationCategory.ts";
import { usePayPalEligibility } from "../usePayPalEligibility";
import { NewPayoutRadioButton } from "./NewPayoutRadioButton";
import { newPayoutItem } from "./constants";

interface Props {
    value: PayoutItem;
    setValue: (value: PayoutItem) => void;
}

export const NewPayoutRadioGroup: FunctionComponent<Props> = (props) => {
    const isPayPalAllowed = usePayPalEligibility();
    const {
        cmsData: {
            pricing: {
                bankTransfer,
                payPal,
                virtualCreditCardOnMultipleBookings,
                virtualCreditCardOnSingleBookings,
                agodaECard,
                eCardMultipleBookingsDescription,
                eCardSingleBookingDescription,
                physicalCreditCardDescription,
                bankTransferGroupBookingsDescription,
                payPalGroupBookingsDescription,
                fastestPayout,
            },
        },
    } = useStaticDataAtomValue();

    const {
        experimentsAtom: { isConvergencePricing },
    } = useMetadataAtomValue();
    const { hasAccommodationTypes } = useAccommodationCategory();
    const isHotel = hasAccommodationTypes(AccommodationCategoryEnum.Hotel, AccommodationCategoryEnum.MultipleNha);

    return (
        <VStack gap="16">
            {isConvergencePricing && isHotel && (
                <Fragment>
                    <NewPayoutRadioButton
                        title={virtualCreditCardOnMultipleBookings}
                        description={eCardMultipleBookingsDescription}
                        item={newPayoutItem.UPCMultiple}
                        badge={{
                            label: fastestPayout,
                            variant: "primary.positive",
                            leadingAsset: {
                                id: "fill.symbol.lightning1",
                            },
                        }}
                        {...props}
                    />
                    <NewPayoutRadioButton
                        title={virtualCreditCardOnSingleBookings}
                        description={eCardSingleBookingDescription}
                        item={newPayoutItem.UPCSingle}
                        {...props}
                    />
                    <NewPayoutRadioButton
                        title={agodaECard}
                        description={physicalCreditCardDescription}
                        item={newPayoutItem.ECard}
                        {...props}
                    />
                </Fragment>
            )}
            <NewPayoutRadioButton
                title={bankTransfer}
                description={bankTransferGroupBookingsDescription}
                item={newPayoutItem.Bank}
                {...props}
            />
            {(!isConvergencePricing && isPayPalAllowed) ||
                (isConvergencePricing && isPayPalAllowed && !isHotel && (
                    <NewPayoutRadioButton
                        title={payPal}
                        description={payPalGroupBookingsDescription}
                        item={newPayoutItem.PayPal}
                        {...props}
                    />
                ))}
        </VStack>
    );
};
